import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { ImageDown, Link2, Check, ArrowRight, Loader2, Quote, Share2, ShieldCheck } from "lucide-react";
import { CharterContent } from "../components/Charter";
import { ShareButton } from "../components/ShareButton";
import { Crest } from "../components/Media";
import { TrustBadges } from "../components/TrustBadges";
import api from "../lib/api";

const PublicVoices = () => {
  const [voices, setVoices] = useState(null);
  useEffect(() => { api.get("/public/voices").then(({ data }) => setVoices(data)).catch(() => setVoices([])); }, []);
  if (!voices || voices.length === 0) return null;
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 pb-4" data-testid="public-voices">
      <div className="border-t border-white/10 pt-8">
        <div className="flex items-center gap-2 mb-1">
          <Quote size={16} className="text-[#14A89A]" />
          <h2 className="font-heading text-lg text-white">Voices from the Signal</h2>
        </div>
        <p className="font-body text-sm text-zinc-500 mb-5">In their own words, from sovereigns already inside.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {voices.map((v, i) => (
            <div key={i} className="border border-white/10 bg-white/[0.02] p-4" data-testid="public-voice-item">
              <p className="font-body text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{v.format === "quote" ? `\u201c${v.quote}\u201d` : v.quote}</p>
              <p className="font-heading text-xs text-[#14A89A] mt-3">{v.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function PublicCompass() {
  const navigate = useNavigate();
  const captureRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/c`;

  // /security deep-links here with a #security hash, scroll to the encryption
  // section once the shared Compass content has rendered.
  useEffect(() => {
    if (window.location.hash !== "#security") return;
    const t = setTimeout(() => {
      const el = document.getElementById("security");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, []);

  const canShareImage = React.useMemo(() => {
    try {
      return typeof navigator !== "undefined" && !!navigator.canShare && navigator.canShare({ files: [new File([""], "c.png", { type: "image/png" })] });
    } catch {
      return false;
    }
  }, []);
  const ImgIcon = canShareImage ? Share2 : ImageDown;

  const shareOrDownload = async (width, key, filename) => {
    const node = captureRef.current;
    if (!node) return;
    setBusy(key);
    const prevWidth = node.style.width;
    const prevMax = node.style.maxWidth;
    try {
      node.style.width = `${width}px`;
      node.style.maxWidth = `${width}px`;
      await new Promise((r) => setTimeout(r, 350));
      const dataUrl = await toPng(node, { pixelRatio: 2, backgroundColor: "#0A0A0A", cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        // Phones: open the native share sheet with the image attached, no download step.
        try {
          await navigator.share({ files: [file], title: "Sovereign Signal", text: "Sovereign Signal, a private, end-to-end encrypted network for sovereigns. Take a look:" });
        } catch { /* user dismissed the share sheet */ }
      } else {
        // Desktops without file-sharing: fall back to a download.
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e) {
      // no-op; user can retry
    } finally {
      node.style.width = prevWidth;
      node.style.maxWidth = prevMax;
      setBusy("");
    }
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="public-compass-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crest className="h-8 w-8" />
            <div className="leading-tight">
              <div className="font-heading text-sm tracking-tight text-white">SOVEREIGN</div>
              <div className="font-body text-[10px] tracking-[0.35em] text-[#14A89A]">SIGNAL</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            data-testid="public-request-invite-top"
            className="inline-flex items-center gap-2 bg-[#14A89A] hover:bg-[#2BC4B5] text-[#0A0A0A] font-medium font-body text-xs md:text-sm px-4 py-2.5 transition-colors"
          >
            Request an invite <ArrowRight size={15} />
          </button>
        </div>
      </header>

      {/* Warm welcome for first-time vetters */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-6">
        <button
          onClick={() => { const el = document.getElementById("security"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}
          data-testid="compass-verify-security"
          className="w-full text-left flex items-center gap-3 border border-[#14A89A]/25 bg-[#14A89A]/[0.05] hover:bg-[#14A89A]/[0.09] transition-colors px-4 py-3 group"
        >
          <ShieldCheck size={18} className="text-[#14A89A] shrink-0" strokeWidth={1.5} />
          <span className="font-body text-sm text-zinc-300 leading-snug">New here? You're welcome to inspect exactly how our encryption works before you ever apply. <span className="text-[#14A89A] group-hover:text-[#2BC4B5]">Read it in plain words →</span></span>
        </button>
      </div>

      {/* Share actions */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-8">
        <p className="font-body text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Share the Compass</p>
        <div className="flex flex-wrap items-center gap-2.5">
          <ShareButton url={shareUrl} title="Sovereign Signal" text="Sovereign Signal, a private, end-to-end encrypted network for sovereigns building new systems. Take a look:" testid="compass-share-button" hideIfUnsupported />
          <button onClick={copyLink} data-testid="copy-link-button" className="inline-flex items-center gap-2 border border-white/15 text-zinc-200 hover:bg-white/5 font-body text-sm px-4 py-2.5 transition-colors">
            {copied ? <Check size={15} className="text-[#14A89A]" /> : <Link2 size={15} />} {copied ? "Link copied" : "Copy link"}
          </button>
        </div>
        <div className="mt-4">
          <p className="font-body text-xs text-zinc-500 mb-2">{canShareImage ? "Share the Compass as an image" : "Download image to share"}</p>
          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={() => shareOrDownload(480, "phone", "sovereign-signal-compass-phone.png")} disabled={!!busy} data-testid="download-phone-button" className="inline-flex items-center gap-2 border border-white/15 text-zinc-200 hover:bg-white/5 font-body text-sm px-4 py-2.5 transition-colors disabled:opacity-40">
              {busy === "phone" ? <Loader2 size={15} className="animate-spin" /> : <ImgIcon size={15} />} Phone
            </button>
            <button onClick={() => shareOrDownload(760, "wide", "sovereign-signal-compass-wide.png")} disabled={!!busy} data-testid="download-wide-button" className="inline-flex items-center gap-2 border border-white/15 text-zinc-200 hover:bg-white/5 font-body text-sm px-4 py-2.5 transition-colors disabled:opacity-40">
              {busy === "wide" ? <Loader2 size={15} className="animate-spin" /> : <ImgIcon size={15} />} Wide
            </button>
          </div>
        </div>
        <p className="font-body text-xs text-zinc-600 mt-3">Tip: the link is best for posts and DMs; the image is a clean, full copy of the Compass, {canShareImage ? "tap Phone or Wide to send it straight to any app." : "download it to send or add to a Story."}</p>
      </div>

      {/* Captured content */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div ref={captureRef} className="mx-auto bg-[#0A0A0A] px-5 py-8 sm:px-8" style={{ maxWidth: 760 }} data-testid="compass-capture">
          <CharterContent onDonate={() => navigate("/")} />
          <div className="border-t border-white/10 mt-8 pt-6 text-center">
            <p className="font-body text-sm text-zinc-400">A private, encrypted network for sovereigns. By invitation.</p>
            <p className="font-body text-[11px] text-zinc-600 mt-3">sovereignsignal.app</p>
          </div>
        </div>
      </div>

      {/* Voices, real member words, placed to build resonance before the ask */}
      <PublicVoices />

      {/* Final call to action */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 pb-16 pt-4 text-center">
        <button
          onClick={() => navigate("/")}
          data-testid="public-request-invite-bottom"
          className="inline-flex items-center gap-2 bg-[#14A89A] hover:bg-[#2BC4B5] text-[#0A0A0A] font-medium font-body text-sm px-6 py-3 transition-colors"
        >
          Request an invite <ArrowRight size={16} />
        </button>
      </div>

      {/* Footer trust badges (public marketing pages only) */}
      <footer data-testid="compass-footer" className="max-w-3xl mx-auto px-4 md:px-8 pb-16 border-t border-white/10 pt-8">
        <TrustBadges />
      </footer>
    </div>
  );
}
